/**
 * Lead Finder Router Component
 *
 * Transparently routes to the correct Lead Finder interface based on admin configuration.
 * Users see a single "Lead Finder" menu item, but the backend determines which actor to use.
 *
 * Supports:
 * - Peaky Leads (existing implementation)
 * - Pipeline Labs (new implementation)
 */

import React, { useState, useEffect } from 'react';
import LeadFinderPage from './LeadFinderPage'; // Peaky Leads implementation
import PipelineLabsLeadFinderPage from './PipelineLabsLeadFinderPage'; // Pipeline Labs implementation

const LeadFinderRouter = () => {
  const [activeActor, setActiveActor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActiveActor = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || ''}/api/lead-finder/active-actor`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch active actor configuration');
        }

        const data = await response.json();
        setActiveActor(data.active_actor); // "peaky" or "pipelinelabs"
      } catch (err) {
        console.error('Error fetching active actor:', err);
        setError(err.message);
        // Default to Peaky on error
        setActiveActor('peaky');
      } finally {
        setLoading(false);
      }
    };

    fetchActiveActor();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Lead Finder...</p>
        </div>
      </div>
    );
  }

  // Error state (fallback to Peaky)
  if (error) {
    console.warn('Lead Finder router error, falling back to Peaky Leads:', error);
  }

  // Route to appropriate implementation
  if (activeActor === 'pipelinelabs') {
    return <PipelineLabsLeadFinderPage />;
  }

  // Default to Peaky Leads
  return <LeadFinderPage />;
};

export default LeadFinderRouter;
